import React from "react";
import { Box, Button, ButtonBase, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useTranslation } from "react-i18next";
import TaxGuruDialog from "../../../components/TaxGuruDialog";
import useIAmAccountant from "../function/useIAmAccountant";
import { useTaxDispatch } from "../../../redux/general/reduxStore";
import { setUserAccountant } from "../../../redux/userSlice";
import { submitAccountantOnboarding } from "../../../backend/taxguru/client/taxAccountantOnboardingClient";
import { TaxAccountantOnboardingRequest } from "../../../backend/taxguru/models/TaxAccountantOnboardingRequest";

type Phase = "form" | "done" | "closed";

type TileProps = {
    selected: boolean;
    onClick: () => void;
    radius: number;
    children: React.ReactNode;
};

const Tile = ({ selected, onClick, radius, children }: TileProps) => (
    <ButtonBase
        onClick={onClick}
        aria-pressed={selected}
        sx={(theme) => ({
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "center",
            textAlign: "left",
            padding: 1.5,
            border: 1,
            borderColor: selected ? theme.palette.primary.main : theme.palette.divider,
            backgroundColor: selected ? theme.palette.action.selected : "transparent",
            borderRadius: `${radius}px`,
        })}
    >
        {children}
    </ButtonBase>
);

/**
 * Dialog shown to licence admins who registered themselves, until they fill it in.
 * Closing it with "Not now" does not touch the database, so it opens again after the next login.
 */
const OnboardingModal = () => {
    const { t } = useTranslation();
    const { accountant } = useIAmAccountant();
    const dispatch = useTaxDispatch();
    const [phase, setPhase] = React.useState<Phase>("form");
    const [usageType, setUsageType] = React.useState<TaxAccountantOnboardingRequest.usageType>();
    const [monthlyDocuments, setMonthlyDocuments] = React.useState<TaxAccountantOnboardingRequest.monthlyDocuments>();
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const required = !!accountant?.licenceAdmin && !!accountant?.onboardingRequired;
    const open = phase === "done" || (phase === "form" && required);

    const onSubmit = () => {
        if (!usageType || !monthlyDocuments || !accountant) {
            return;
        }
        setIsSubmitting(true);
        submitAccountantOnboarding({ usageType, monthlyDocuments })
            .then(() => {
                setPhase("done");
                dispatch(setUserAccountant({ ...accountant, onboardingRequired: false }));
            })
            .finally(() => setIsSubmitting(false));
    };

    const close = () => setPhase("closed");

    if (!open) {
        return null;
    }

    if (phase === "done") {
        return (
            <TaxGuruDialog disableClosing open variant="confirm">
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 2, paddingY: 2 }}>
                    <CheckCircleOutlineIcon color="success" sx={{ fontSize: 72 }} />
                    <Typography variant="h5">{t("onboardingDialog.done.title")}</Typography>
                    <Typography>{t("onboardingDialog.done.text")}</Typography>
                    <Button variant="contained" onClick={close}>
                        {t("onboardingDialog.done.start")}
                    </Button>
                </Box>
            </TaxGuruDialog>
        );
    }

    return (
        <TaxGuruDialog
            disableClosing
            open
            variant="forForm"
            sx={{ "& .MuiPaper-root:first-of-type": { width: { xs: "100%", md: "800px" } } }}
            actions={
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", paddingX: { xs: 1, md: 4 }, paddingBottom: 2 }}>
                    <Button color="inherit" sx={{ textDecoration: "underline", fontWeight: "light" }} onClick={close} disabled={isSubmitting}>
                        {t("onboardingDialog.notNow")}
                    </Button>
                    <Button variant="contained" sx={{ paddingX: 2.5, paddingY: 1.5 }} disabled={!usageType || !monthlyDocuments || isSubmitting} onClick={onSubmit}>
                        {t("onboardingDialog.saveAndContinue")}
                    </Button>
                </Box>
            }
        >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 4, paddingX: { xs: 1, md: 4 }, paddingY: 2 }}>
                <Typography component="h2" sx={{ fontSize: "2rem", fontWeight: 500, lineHeight: 1.2 }}>
                    {t("onboardingDialog.title")}
                </Typography>
                <Box>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ marginBottom: 1 }}>
                        {t("onboardingDialog.usageTypeLabel")}
                    </Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1.5 }}>
                        {Object.values(TaxAccountantOnboardingRequest.usageType).map((value) => (
                            <Tile key={value} selected={usageType === value} onClick={() => setUsageType(value)} radius={8}>
                                <Typography fontWeight="bold">{t(`onboardingDialog.usageType.${value}.name`)}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t(`onboardingDialog.usageType.${value}.description`)}
                                </Typography>
                            </Tile>
                        ))}
                    </Box>
                </Box>
                <Box>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ marginBottom: 1 }}>
                        {t("onboardingDialog.monthlyDocumentsLabel")}{" "}
                        <Typography component="span" fontWeight="normal" color="text.secondary">
                            {t("onboardingDialog.monthlyDocumentsHint")}
                        </Typography>
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                        {Object.values(TaxAccountantOnboardingRequest.monthlyDocuments).map((value) => (
                            <Tile key={value} selected={monthlyDocuments === value} onClick={() => setMonthlyDocuments(value)} radius={24}>
                                <Typography sx={{ paddingX: 1 }}>{t(`onboardingDialog.monthlyDocuments.${value}`)}</Typography>
                            </Tile>
                        ))}
                    </Box>
                </Box>
            </Box>
        </TaxGuruDialog>
    );
};

export default OnboardingModal;
